import { useCallback, useEffect, useState } from 'react';
import type { Organisator, Profiel } from '@qc/db';
import { herstelLinkBijStart, supabase } from '../supabase.js';

export type Sessiestatus = 'laden' | 'uitgelogd' | 'zonder_profiel' | 'klaar';

export interface Sessie {
  status: Sessiestatus;
  gebruikerId: string | null;
  email: string | null;
  profiel: Profiel | null;
  organisator: Organisator | null;
  /** Kwam de bezoeker via de link uit de herstelmail en moet hij nog een wachtwoord kiezen? */
  herstel: boolean;
  /** Het nieuwe wachtwoord staat; terug naar het gewone dashboard. */
  herstelKlaar: () => void;
  ververs: () => Promise<void>;
  logUit: () => Promise<void>;
}

/**
 * Wie is er ingelogd, en heeft die al een organisatorprofiel?
 *
 * Een gebruiker die zich net heeft aangemeld heeft wel een inlog maar nog geen
 * bedrijfsgegevens. Dat is de status "zonder_profiel": dan volgt het
 * aanmeldscherm, en pas daarna het dashboard.
 */
export function useSessie(): Sessie {
  const [status, setStatus] = useState<Sessiestatus>('laden');
  const [gebruikerId, setGebruikerId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [profiel, setProfiel] = useState<Profiel | null>(null);
  const [organisator, setOrganisator] = useState<Organisator | null>(null);
  // De link uit de mail kan er al zijn vóór deze hook luistert (de client leest
  // hem bij het starten), vandaar de beginwaarde uit de adresbalk én de gebeurtenis.
  const [herstel, setHerstel] = useState(herstelLinkBijStart);

  const ververs = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const gebruiker = data.session?.user ?? null;

    if (gebruiker === null) {
      setGebruikerId(null);
      setEmail(null);
      setProfiel(null);
      setOrganisator(null);
      setStatus('uitgelogd');
      return;
    }

    setGebruikerId(gebruiker.id);
    setEmail(gebruiker.email ?? null);

    const { data: profielen } = await supabase
      .from('profielen')
      .select('*')
      .eq('gebruiker_id', gebruiker.id)
      .limit(1);

    const gevonden = profielen?.[0] ?? null;
    setProfiel(gevonden);

    // De beheerder heeft geen organisatorprofiel; die hoort er ook niet te
    // hebben. Hij ziet alle evenementen en rekent ze af.
    if (gevonden?.rol === 'admin') {
      setOrganisator(null);
      setStatus('klaar');
      return;
    }

    if (gevonden?.organizer_id == null) {
      setOrganisator(null);
      setStatus('zonder_profiel');
      return;
    }

    const { data: organisatoren } = await supabase
      .from('organisatoren')
      .select('*')
      .eq('id', gevonden.organizer_id)
      .limit(1);

    setOrganisator(organisatoren?.[0] ?? null);
    setStatus('klaar');
  }, []);

  useEffect(() => {
    void (async () => {
      await ververs();
    })();
    const { data } = supabase.auth.onAuthStateChange((gebeurtenis) => {
      if (gebeurtenis === 'PASSWORD_RECOVERY') setHerstel(true);
      void (async () => {
        await ververs();
      })();
    });
    return () => data.subscription.unsubscribe();
  }, [ververs]);

  const logUit = useCallback(async () => {
    setHerstel(false);
    await supabase.auth.signOut();
    await ververs();
  }, [ververs]);

  const herstelKlaar = useCallback(() => setHerstel(false), []);

  return {
    status,
    gebruikerId,
    email,
    profiel,
    organisator,
    herstel,
    herstelKlaar,
    ververs,
    logUit,
  };
}
